import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProConfirmSheet } from './primitives';
import { registerProLeaveHandler } from './proBackPolicy';

export interface UseProUnsavedLeaveGuardOptions {
  /** When true, leave attempts via `requestProLeave` / hardware back prompt first. */
  isDirty: boolean;
  /** Handler priority (higher runs first). Default 100 — above drawer-level concerns. */
  priority?: number;
}

/**
 * Registers a Pro leave handler that prompts before discarding unsaved edits.
 *
 * Shares the path with toolbar back, tab/drawer nav (`requestProLeave`), and
 * Android hardware back (`requestProHardwareBack`).
 *
 * Returns a `ProConfirmSheet` element to render alongside the page.
 * Confirm runs the pending `proceed`; Cancel / sheet dismiss keeps the edits.
 */
export function useProUnsavedLeaveGuard({
  isDirty,
  priority = 100,
}: UseProUnsavedLeaveGuardOptions): React.ReactElement {
  const { t } = useTranslation('pro');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingProceed = useRef<(() => void) | null>(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    return registerProLeaveHandler((proceed) => {
      if (!isDirtyRef.current) return false;
      pendingProceed.current = proceed;
      setConfirmOpen(true);
      return true;
    }, priority);
  }, [priority]);

  // If the form becomes clean while the sheet is open (e.g. reset), drop it.
  useEffect(() => {
    if (!isDirty && confirmOpen) {
      setConfirmOpen(false);
      pendingProceed.current = null;
    }
  }, [isDirty, confirmOpen]);

  const handleDismiss = () => {
    setConfirmOpen(false);
    pendingProceed.current = null;
  };

  const handleConfirm = () => {
    const go = pendingProceed.current;
    pendingProceed.current = null;
    setConfirmOpen(false);
    go?.();
  };

  return (
    <ProConfirmSheet
      isOpen={confirmOpen}
      onDismiss={handleDismiss}
      title={t('common.unsavedChangesTitle')}
      body={t('common.unsavedChangesBody')}
      confirmLabel={t('common.discard')}
      cancelLabel={t('common.cancel')}
      onConfirm={handleConfirm}
      variant="danger"
    />
  );
}
